import React, { useEffect, useState } from 'react';
import { cleaningApi, formatApiError } from '../services/api';
import { Sparkle, Plus, Trash, ArrowUp, ArrowDown, FloppyDisk, ArrowClockwise } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

/**
 * CleaningChecklistCard — editier die 10 Standard-Reinigungspunkte
 * (oder eigene). Speichert unter settings.cleaning_checklist.
 */
export default function CleaningChecklistCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await cleaningApi.getTemplate();
      setItems((r.data || []).map(i => ({ ...i })));
      setDirty(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateItem = (idx, patch) => {
    setItems(list => list.map((it, i) => i === idx ? { ...it, ...patch } : it));
    setDirty(true);
  };
  const addItem = () => {
    const maxOrder = items.reduce((m, i) => Math.max(m, i.order || 0), 0);
    setItems(list => [...list, { key: `custom_${Date.now()}`, label: 'Neuer Punkt', required: false, order: maxOrder + 10 }]);
    setDirty(true);
  };
  const removeItem = (idx) => {
    if (!window.confirm('Diesen Punkt wirklich entfernen?')) return;
    setItems(list => list.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= items.length) return;
    setItems(list => {
      const next = [...list];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next.map((it, i) => ({ ...it, order: (i + 1) * 10 }));
    });
    setDirty(true);
  };

  const save = async () => {
    // Validierung
    if (items.length === 0) { toast.error('Mindestens ein Item erforderlich'); return; }
    const keys = new Set();
    for (const it of items) {
      if (!it.key || !it.label) { toast.error('Alle Punkte brauchen Key und Bezeichnung'); return; }
      const k = String(it.key).trim().toLowerCase();
      if (keys.has(k)) { toast.error(`Duplikater Key: ${it.key}`); return; }
      keys.add(k);
    }
    setSaving(true);
    try {
      const payload = items.map((it, i) => ({
        key: String(it.key).trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_'),
        label: String(it.label).trim(),
        required: !!it.required,
        order: it.order != null ? Number(it.order) : (i + 1) * 10,
      }));
      const r = await cleaningApi.saveTemplate(payload);
      setItems(r.data);
      setDirty(false);
      toast.success('Reinigungs-Checkliste gespeichert');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const reset = () => {
    if (!window.confirm('Änderungen verwerfen und neu laden?')) return;
    load();
  };

  return (
    <div className="card-swiss p-6" data-testid="cleaning-checklist-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
            <Sparkle weight="fill" size={20} className="text-blue-700" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Reinigungs-Checkliste</h2>
            <p className="text-sm text-[#525866]">Punkte, die nach jeder Rückgabe abgehakt werden. „Pflicht" muss abgehakt sein, um Fahrzeug automatisch wieder verfügbar zu machen.</p>
          </div>
        </div>
        <div className="flex gap-2">
          {dirty && (
            <Button size="sm" variant="outline" onClick={reset} data-testid="cleaning-reset-btn">
              <ArrowClockwise weight="bold" size={14} className="mr-1"/> Verwerfen
            </Button>
          )}
          <Button size="sm" className="btn-primary" onClick={save} disabled={!dirty || saving} data-testid="cleaning-save-btn">
            <FloppyDisk weight="bold" size={14} className="mr-1"/> {saving ? 'Speichere…' : 'Speichern'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="h-24 skeleton" />
      ) : (
        <>
          <div className="border border-[#E2E4E9] rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F8FA] text-xs text-[#525866]">
                <tr>
                  <th className="text-left px-2 py-2 w-16">Reihenfolge</th>
                  <th className="text-left px-2 py-2">Bezeichnung</th>
                  <th className="text-left px-2 py-2 hidden md:table-cell">Key</th>
                  <th className="text-center px-2 py-2 w-16">Pflicht</th>
                  <th className="text-right px-2 py-2 w-24">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} className={`border-t border-[#E2E4E9] ${it.required ? 'bg-orange-50/30' : ''}`} data-testid={`cleaning-row-${idx}`}>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-[#525866] hover:text-[#111418] disabled:opacity-30" title="Nach oben"><ArrowUp size={12} weight="bold"/></button>
                        <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="p-1 text-[#525866] hover:text-[#111418] disabled:opacity-30" title="Nach unten"><ArrowDown size={12} weight="bold"/></button>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        value={it.label}
                        onChange={e => updateItem(idx, { label: e.target.value })}
                        className="input-swiss h-8"
                        data-testid={`cleaning-label-${idx}`}
                      />
                    </td>
                    <td className="px-2 py-2 hidden md:table-cell">
                      <Input
                        value={it.key}
                        onChange={e => updateItem(idx, { key: e.target.value })}
                        className="input-swiss h-8 font-mono text-xs"
                        placeholder="key_slug"
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={!!it.required}
                        onChange={e => updateItem(idx, { required: e.target.checked })}
                        className="w-4 h-4 accent-[#E60000] cursor-pointer"
                        data-testid={`cleaning-required-${idx}`}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        onClick={() => removeItem(idx)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Punkt entfernen"
                        data-testid={`cleaning-delete-${idx}`}
                      >
                        <Trash size={14} weight="bold"/>
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-6 text-[#8A94A6]">Keine Punkte. „Punkt hinzufügen" klicken.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Button size="sm" variant="outline" onClick={addItem} className="mt-3" data-testid="cleaning-add-btn">
            <Plus weight="bold" size={14} className="mr-1"/> Punkt hinzufügen
          </Button>
        </>
      )}
    </div>
  );
}
